/**
* @license
* 
* (C) Copyright IBM Corp. 2017, 2025. All Rights Reserved.
* 
* Licensed under the Apache License, Version 2.0 (the "License"); you may not use this file except
* in compliance with the License. You may obtain a copy of the License at
* 
* http://www.apache.org/licenses/LICENSE-2.0
* 
* Unless required by applicable law or agreed to in writing, software distributed under the License
* is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express
* or implied. See the License for the specific language governing permissions and limitations under
* the License.
* 
* @carbon/ai-chat 0.3.2
* 
* Built: Jun 11 2025 5:48 pm +00:00
* 
* 
*/

import { useEffect } from 'react';

/**
 * The set of possible message input types in a request.
 */
var MessageInputType;
(function (MessageInputType) {
    /**
     * Represents a simple text message.
     */
    MessageInputType["TEXT"] = "text";
    /**
     * Represents an event message that can be used to send control, updates, or action information to the back-end.
     */
    MessageInputType["EVENT"] = "event";
    /**
     * Represents a skill_complete message that can be used to indicate that a form-based skill-based action has completed.
     */
    MessageInputType["SKILL_COMPLETE"] = "skill_complete";
})(MessageInputType || (MessageInputType = {}));
/**
 * The set of possible message types in a response from IBM watsonx Assistant.
 */
var MessageResponseTypes;
(function (MessageResponseTypes) {
    /**
     * Represents a basic text response. The given text may contain rich content such as markdown.
     */
    MessageResponseTypes["TEXT"] = "text";
    /**
     * A response that requests the user choose an option from a list. The list of options may be presented as a list
     * of buttons or it may be from a drop-down.
     */
    MessageResponseTypes["OPTION"] = "option";
    /**
     * Indicates that the conversation should be escalated to a human agent and offers that opportunity to the user.
     */
    MessageResponseTypes["CONNECT_TO_AGENT"] = "connect_to_agent";
    /**
     * Displays an image to the user.
     */
    MessageResponseTypes["IMAGE"] = "image";
    /**
     * Indicates that the chat should display a pause at this point in the conversation before displaying additional
     * items.
     */
    MessageResponseTypes["PAUSE"] = "pause";
    /**
     * A user defined response will be displayed according to custom logic in the client.
     */
    MessageResponseTypes["USER_DEFINED"] = "user_defined";
    /**
     * Displays the contents of an iframe to the user.
     */
    MessageResponseTypes["IFRAME"] = "iframe";
    /**
     * Displays a video to the user using a video player.
     */
    MessageResponseTypes["VIDEO"] = "video";
    /**
     * Displays an audio clip to the user using an audio player.
     */
    MessageResponseTypes["AUDIO"] = "audio";
    /**
     * Asks the user to provide a date. This may result in a date picker being presented to the user.
     */
    MessageResponseTypes["DATE"] = "date";
    /**
     * Displays a table of data to the user.
     */
    MessageResponseTypes["TABLE"] = "table";
    /**
     * Displays a general error message to the user and include developer info to be logged and to debug.
     */
    MessageResponseTypes["INLINE_ERROR"] = "inline_error";
    /**
     * Displays a card that can contain other response types.
     */
    MessageResponseTypes["CARD"] = "card";
    /**
     * Displays a carousel of cards that can contain other response types.
     */
    MessageResponseTypes["CAROUSEL"] = "carousel";
    /**
     * Displays a button that can either send a message back to the backend, open a url, or throw a client side event.
     */
    MessageResponseTypes["BUTTON"] = "button";
    /**
     * Ability to layout response types inside a grid.
     */
    MessageResponseTypes["GRID"] = "grid";
})(MessageResponseTypes || (MessageResponseTypes = {}));
/**
 * The set of possible response preferences for an options response.
 */
var OptionItemPreference;
(function (OptionItemPreference) {
    /**
     * Indicates the options should be displayed as a drop-down.
     */
    OptionItemPreference["DROPDOWN"] = "dropdown";
    /**
     * Indicates the options should be displayed as buttons.
     */
    OptionItemPreference["BUTTON"] = "button";
})(OptionItemPreference || (OptionItemPreference = {}));
/**
 * The different ways an iframe item may be displayed.
 */
var IFrameItemDisplayOption;
(function (IFrameItemDisplayOption) {
    /**
     * The iframe is displayed inline in the main message list.
     */
    IFrameItemDisplayOption["INLINE"] = "inline";
    /**
     * The iframe is displayed in a separate panel after showing a card in the main message list.
     */
    IFrameItemDisplayOption["PANEL"] = "panel";
})(IFrameItemDisplayOption || (IFrameItemDisplayOption = {}));
var ButtonItemKind;
(function (ButtonItemKind) {
    ButtonItemKind["DEFAULT"] = "default";
    ButtonItemKind["SECONDARY"] = "secondary";
    ButtonItemKind["TERTIARY"] = "tertiary";
    ButtonItemKind["DANGER"] = "danger";
    ButtonItemKind["LINK"] = "link";
})(ButtonItemKind || (ButtonItemKind = {}));
var ButtonItemType;
(function (ButtonItemType) {
    ButtonItemType["POST_BACK"] = "post_back";
    ButtonItemType["CUSTOM_EVENT"] = "custom_event";
    ButtonItemType["SHOW_PANEL"] = "show_panel";
    ButtonItemType["URL"] = "url";
})(ButtonItemType || (ButtonItemType = {}));
var WidthOptions;
(function (WidthOptions) {
    /**
     * Width the size of the floating chat for smaller content.
     */
    WidthOptions["SMALL"] = "small";
    /**
     * Max width of 438px, 2/3rd of the width of chat in fullscreen view with hasContentMaxWidth: true.
     */
    WidthOptions["MEDIUM"] = "medium";
    /**
     * Max width of 585px, the full with of chat in fullscreen view with hasContentMaxWidth: true.
     */
    WidthOptions["LARGE"] = "large";
})(WidthOptions || (WidthOptions = {}));

/**
 * Constants for the Carbon FileStatus type because they weren't kind enough to include their own enum.
 */
var FileStatusValue;
(function (FileStatusValue) {
    FileStatusValue["COMPLETE"] = "complete";
    FileStatusValue["EDIT"] = "edit";
    FileStatusValue["UPLOADING"] = "uploading";
    FileStatusValue["SUCCESS"] = "success";
})(FileStatusValue || (FileStatusValue = {}));
/**
 * The different views that can be shown by web chat.
 */
var ViewType;
(function (ViewType) {
    /**
     * The launcher view is used to open the main window or tour.
     */
    ViewType["LAUNCHER"] = "launcher";
    /**
     * The main window view is used to ask WA questions and converse with an agent, as well as many other things. The
     * string value is kept camel case to align with the viewState mainWindow property, both of which are used by the
     * {@link ChatActions.changeView} function.
     */
    ViewType["MAIN_WINDOW"] = "mainWindow";
    /**
     * The tour view is used to guide the end user through a task.
     */
    ViewType["TOUR"] = "tour";
})(ViewType || (ViewType = {}));
/**
 * The different variations of the launcher that can exist.
 */
var LauncherType;
(function (LauncherType) {
    /**
     * The launcher that expands to a "complex" variation on desktop.
     */
    LauncherType["DESKTOP"] = "desktop";
    /**
     * The launcher that expands to an "extended" variation on mobile.
     */
    LauncherType["MOBILE"] = "mobile";
})(LauncherType || (LauncherType = {}));

/**
 * The types of elements that can be added to the chat header element.
 */
var ChatHeaderObjectType;
(function (ChatHeaderObjectType) {
    ChatHeaderObjectType["LINK"] = "link";
    ChatHeaderObjectType["MENU"] = "menu";
    ChatHeaderObjectType["BUTTON"] = "button";
    ChatHeaderObjectType["RADIO_GROUP"] = "radio-group";
})(ChatHeaderObjectType || (ChatHeaderObjectType = {}));
var ChatHeaderObjectType$1 = ChatHeaderObjectType;

var WriteableElementName;
(function (WriteableElementName) {
    /**
     * An element that appears in the AI theme only and is shown beneath the title and description in the AI tooltip
     * content.
     */
    WriteableElementName["AI_TOOLTIP_AFTER_DESCRIPTION_ELEMENT"] = "aiTooltipAfterDescriptionElement";
    /**
     * An element that appears in the main message body directly above the welcome node.
     */
    WriteableElementName["WELCOME_NODE_BEFORE_ELEMENT"] = "welcomeNodeBeforeElement";
    /**
     * An element that appears in the header on a new line. Only visible while talking to the bot.
     */
    WriteableElementName["HEADER_BOTTOM_ELEMENT"] = "headerBottomElement";
    /**
     * An element that appears after the messages area and before the input area.
     */
    WriteableElementName["BEFORE_INPUT_ELEMENT"] = "beforeInputElement";
    /**
     * An element that appears above the input field on the home screen.
     */
    WriteableElementName["HOME_SCREEN_BEFORE_INPUT_ELEMENT"] = "homeScreenBeforeInputElement";
    /**
     * An element that appears on the home screen after the conversation starters.
     */
    WriteableElementName["HOME_SCREEN_AFTER_STARTERS_ELEMENT"] = "homeScreenAfterStartersElement";
    /**
     * An element that appears on the home screen above the welcome message and conversation starters.
     */
    WriteableElementName["HOME_SCREEN_HEADER_BOTTOM_ELEMENT"] = "homeScreenHeaderBottomElement";
    /**
     * An element to be housed in the custom panel.
     */
    WriteableElementName["CUSTOM_PANEL_ELEMENT"] = "customPanelElement";
})(WriteableElementName || (WriteableElementName = {}));
/**
 * The different input fields (text areas) in the application.
 */
var InputFieldType;
(function (InputFieldType) {
    /**
     * The main input field on the message list.
     */
    InputFieldType["MESSAGE"] = "message";
    /**
     * The input field on the home screen.
     */
    InputFieldType["HOME_SCREEN"] = "homeScreen";
})(InputFieldType || (InputFieldType = {}));

/**
 * The types of corners web chat can have.
 */
var CornersType;
(function (CornersType) {
    CornersType["ROUND"] = "round";
    CornersType["SQUARE"] = "square";
})(CornersType || (CornersType = {}));

var BusEventType;
(function (BusEventType) {
    BusEventType["CLOSE_PANEL_BUTTON_TOGGLED"] = "closePanelButton:toggled";
    BusEventType["PRE_RECEIVE"] = "pre:receive";
    BusEventType["RECEIVE"] = "receive";
    BusEventType["PRE_SEND"] = "pre:send";
    BusEventType["SEND"] = "send";
    BusEventType["VIEW_PRE_CHANGE"] = "view:pre:change";
    BusEventType["VIEW_CHANGE"] = "view:change";
    BusEventType["MESSAGE_ITEM_CUSTOM"] = "messageItemCustom";
    BusEventType["CUSTOM_RESPONSE"] = "customResponse";
    BusEventType["USER_DEFINED_RESPONSE"] = "userDefinedResponse";
    BusEventType["PROCESS_OPEN"] = "process:open";
    BusEventType["PROCESS_CLOSE"] = "process:close";
    BusEventType["PROCESS_CANCEL"] = "process:cancel";
    BusEventType["HISTORY_BEGIN"] = "history:begin";
    BusEventType["HISTORY_END"] = "history:end";
    BusEventType["IDENTITY_EXPIRED"] = "identityTokenExpired";
    BusEventType["PRE_RESTART_CONVERSATION"] = "pre:restartConversation";
    BusEventType["RESTART_CONVERSATION"] = "restartConversation";
    BusEventType["CHAT_READY"] = "chat:ready";
    BusEventType["CUSTOM_PANEL_PRE_OPEN"] = "customPanel:pre:open";
    BusEventType["CUSTOM_PANEL_OPEN"] = "customPanel:open";
    BusEventType["CUSTOM_PANEL_PRE_CLOSE"] = "customPanel:pre:close";
    BusEventType["CUSTOM_PANEL_CLOSE"] = "customPanel:close";
    BusEventType["SESSION_EXPIRED"] = "sessionExpired";
    BusEventType["TOUR_START"] = "tour:start";
    BusEventType["TOUR_END"] = "tour:end";
    BusEventType["TOUR_STEP"] = "tour:step";
    /**
     * This event is fired before web chat processes a message received from a human agent from a service desk.
     */
    BusEventType["AGENT_PRE_RECEIVE"] = "agent:pre:receive";
    /**
     * This event is fired after web chat processes a message received from a human agent from a service desk.
     */
    BusEventType["AGENT_RECEIVE"] = "agent:receive";
    /**
     * This event is fired before web chat sends a message to a human agent from a service desk.
     */
    BusEventType["AGENT_PRE_SEND"] = "agent:pre:send";
    /**
     * This event is fired after web chat sends a message to a human agent from a service desk.
     */
    BusEventType["AGENT_SEND"] = "agent:send";
    /**
     * This event is fired before a chat with a service desk has started. This occurs as soon as the user clicks the
     * "Request agent" button and before any attempt is made to communicate with the service desk.
     */
    BusEventType["AGENT_PRE_START_CHAT"] = "agent:pre:startChat";
    /**
     * This event is fired before a chat with an agent is ended. This occurs after the user has selected "Yes" from the
     * confirmation modal but it can also be fired if the chat is ended by the agent. Note that this is not fired if a
     * request for an agent is cancelled. The agent:endChat event however is fired in that case.
     */
    BusEventType["AGENT_PRE_END_CHAT"] = "agent:pre:endChat";
    /**
     * This event is fired after a chat with an agent has ended. This is fired after {@link AGENT_PRE_END_CHAT} but
     * can be fired both from the user leaving the chat or the agent ending the chat.
     */
    BusEventType["AGENT_END_CHAT"] = "agent:endChat";
    /**
     * This event is fired after web chat calls "areAnyAgentsOnline" for a service desk. It will report the value returned
     * from that call. This is particularly useful if some custom code wants to take action if no agents are online.
     */
    BusEventType["AGENT_ARE_ANY_AGENTS_ONLINE"] = "agent:areAnyAgentsOnline";
    /**
     * This event is fired before a message between the user and a live agent is added to session history. It allows
     * the customer to modify the message in order to do things like remove PII from being stored by IBM watsonx
     * Assistant.
     */
    BusEventType["AGENT_PRE_SESSION_HISTORY"] = "agent:pre:sessionHistory";
    BusEventType["GENESYS_MESSENGER_AUTH_PROVIDER"] = "agent:genesysMessenger:AuthProvider";
    BusEventType["GENESYS_MESSENGER_GET_AUTH_CODE"] = "agent:genesysMessenger:getAuthCode";
    BusEventType["GENESYS_MESSENGER_REAUTHENTICATE"] = "agent:genesysMessenger:reAuthenticate";
    BusEventType["GENESYS_MESSENGER_LOGGED_OUT"] = "agent:genesysMessenger:loggedOut";
    BusEventType["GENESYS_MESSENGER_AUTH_ERROR"] = "agent:genesysMessenger:authError";
    BusEventType["NICE_DFO_GET_AUTH_CODE"] = "agent:niceDFO:getAuthCode";
    BusEventType["CHUNK_USER_DEFINED_RESPONSE"] = "chunk:userDefinedResponse";
    /**
     * This event is fired when the user interacts with the feedback controls on a message. This includes both the feedback
     * buttons (thumbs up/down) as well as the details popup where the user can submit additional information.
     */
    BusEventType["FEEDBACK"] = "feedback";
    /**
     * This event is fired when the "stop streaming" button in the input field is clicked.
     */
    BusEventType["STOP_STREAMING"] = "stopStreaming";
    BusEventType["ALL_EVENTS"] = "*";
})(BusEventType || (BusEventType = {}));
/**
 * The possible reasons why the view may be changed.
 */
var ViewChangeReason;
(function (ViewChangeReason) {
    /**
     * Indicates the web chat has loaded for the first time and a view is trying to open. If {@link openChatByDefault} is
     * true then the main window will be trying to open, otherwise the launcher will be trying to open.
     */
    ViewChangeReason["WEB_CHAT_LOADED"] = "webChatLoaded";
    /**
     * Indicates the user clicked on our built-in launcher button that opened either a tour or the main window.
     */
    ViewChangeReason["LAUNCHER_CLICKED"] = "launcherClicked";
    /**
     * Indicates the view is trying to change after loading session history.
     */
    ViewChangeReason["SESSION_HISTORY"] = "sessionHistory";
    /**
     * Indicates the main window was opened by a call to {@link ChatInstance.openWindow} or
     * {@link ChatInstance.toggleOpen}.
     */
    ViewChangeReason["CALLED_OPEN_WINDOW"] = "calledOpenWindow";
    /**
     * Indicates the main window was opened because a link ID was found that started a specific conversation.
     */
    ViewChangeReason["LINK_ID"] = "linkID";
    /**
     * Indicates the main window was opened from a tour.
     */
    ViewChangeReason["TOUR_OPENED_OTHER_VIEW"] = "tourOpenedOtherView";
    /**
     * Indicates the {@link ChatInstance.restartConversation} method was used while a tour was visible.
     */
    ViewChangeReason["CALLED_RESTART_CONVERSATION"] = "calledRestartConversation";
    /**
     * Indicates the main window is being opened as a result of a channel transfer.
     */
    ViewChangeReason["CHANNEL_TRANSFER"] = "channelTransfer";
    /**
     * Indicates the user clicked on our built-in minimize button that closed to either the launcher or a tour.
     */
    ViewChangeReason["MAIN_WINDOW_MINIMIZED"] = "mainWindowMinimized";
    /**
     * Indicates the main window was closed by a call to {@link ChatInstance.closeWindow} or
     * {@link ChatInstance.toggleOpen}.
     */
    ViewChangeReason["CALLED_CLOSE_WINDOW"] = "calledCloseWindow";
    /**
     * Indicates a tour was started by the start tour button in the main window.
     */
    ViewChangeReason["TOUR_CARD_STARTED_TOUR"] = "tourCardStartedTour";
    /**
     * Indicates a tour was resumed by the resume button in the main window.
     */
    ViewChangeReason["TOUR_CARD_RESUMED_TOUR"] = "tourCardResumedTour";
    /**
     * Indicates a tour was restarted by the restart button in the main window.
     */
    ViewChangeReason["TOUR_CARD_RESTARTED_TOUR"] = "tourCardRestartedTour";
    /**
     * Indicates a tour was started by the {@link startTour} instance method.
     */
    ViewChangeReason["CALLED_START_TOUR"] = "calledStartTour";
    /**
     * Indicates a tour was started by a tour response type that included the 'skip_card' flag.
     */
    ViewChangeReason["TOUR_SKIP_CARD"] = "tourSkipCard";
    /**
     * Indicates the user clicked the close and restart button that minimized to the launcher.
     */
    ViewChangeReason["MAIN_WINDOW_CLOSED_AND_RESTARTED"] = "mainWindowClosedAndRestarted";
    /**
     * Indicates the user clicked on the minimize button within the tour to close to the launcher.
     */
    ViewChangeReason["TOUR_MINIMIZED"] = "tourMinimized";
    /**
     * Indicates the close button was used to close a tour.
     */
    ViewChangeReason["TOUR_CLOSED"] = "tourClosed";
    /**
     * Indicates the done button was used to close a tour.
     */
    ViewChangeReason["TOUR_DONE"] = "tourDone";
    /**
     * Indicates the view was changed by a call to {@link ChatInstance.changeView}.
     */
    ViewChangeReason["CALLED_CHANGE_VIEW"] = "calledChangeView";
    /**
     * Indicates the {@link ChatInstance.tours.endTour} method was used to close a tour.
     */
    ViewChangeReason["CALLED_END_TOUR"] = "calledEndTour";
})(ViewChangeReason || (ViewChangeReason = {}));
/**
 * The different sources that can cause a send event to occur.
 */
var MessageSendSource;
(function (MessageSendSource) {
    /**
     * The user has entered a value from the main input on the message list.
     */
    MessageSendSource["MESSAGE_INPUT"] = "messageInput";
    /**
     * The user has entered a value from the input on the home screen.
     */
    MessageSendSource["HOME_SCREEN_INPUT"] = "homeScreenInput";
    /**
     * The user clicked a button from an option response.
     */
    MessageSendSource["OPTION_BUTTON"] = "optionButton";
    /**
     * The user selected a value from a dropdown for an option response.
     */
    MessageSendSource["OPTION_DROP_DOWN"] = "optionDropDown";
    /**
     * The automatic follow-up message that occurs after a skip-user-input response.
     */
    MessageSendSource["SKIP_USER_INPUT"] = "skipUserInput";
    /**
     * The message was sent as an automatic re-send when web chat is loaded. This occurs when web chat sees that the
     * last message request did not receive a response.
     */
    MessageSendSource["HYDRATE_RESEND"] = "hydrateResend";
    /**
     * The message was sent as an event history update.
     */
    MessageSendSource["HISTORY_UPDATE"] = "historyUpdate";
    /**
     * An external call to the public "instance.send" method was made.
     */
    MessageSendSource["INSTANCE_SEND"] = "instanceSend";
    /**
     * The user chose a value from the date picker.
     */
    MessageSendSource["DATE_PICKER"] = "datePicker";
    /**
     * The user clicked a post back button from a button response.
     */
    MessageSendSource["POST_BACK_BUTTON"] = "postBackButton";
    /**
     * A link ID was detected on the page and an automatic "welcome request" was sent.
     */
    MessageSendSource["LINK_ID"] = "linkID";
    /**
     * The user clicked a starter from the home screen.
     */
    MessageSendSource["HOME_SCREEN_STARTER"] = "homeScreenStarter";
    /**
     * The user made a choice from a disambiguation response.
     */
    MessageSendSource["DISAMBIGUATION"] = "disambiguation";
    /**
     * The startTour method has called.
     */
    MessageSendSource["START_TOUR_METHOD"] = "startTourMethod";
    /**
     * A default request for the welcome message was made.
     */
    MessageSendSource["WELCOME_REQUEST"] = "welcomeRequest";
    /**
     * This is used for message events.
     */
    MessageSendSource["EVENT"] = "event";
    /**
     * Some other source.
     */
    MessageSendSource["OTHER"] = "other";
    /**
     * The user chose an alternate response message from the suggestions menu.
     */
    MessageSendSource["SUGGESTIONS_MESSAGE"] = "suggestionsMessage";
    /**
     * The user chose a home screen starter from the suggestions menu.
     */
    MessageSendSource["SUGGESTIONS_STARTER"] = "suggestionsStarter";
    /**
     * The user chose the contact option from the suggestions menu.
     */
    MessageSendSource["SUGGESTIONS_CONTACT"] = "suggestionsContact";
    /**
     * A 3rd strike occurred and an automatic suggestions contact message was sent.
     */
    MessageSendSource["SUGGESTIONS_3RD_STRIKE"] = "suggestions3rdStrike";
})(MessageSendSource || (MessageSendSource = {}));
/**
 * The possible reasons why a tour could be started. Purposefully not firing this event when the restart button is
 * clicked.
 */
var TourStartReason;
(function (TourStartReason) {
    /**
     * If the {@link startTour} instance method was used to start the tour.
     */
    TourStartReason["START_TOUR_METHOD"] = "start_tour_method";
    /**
     * If the skip_card property was true within the tour json.
     */
    TourStartReason["SKIP_CARD"] = "skip_card";
    /**
     * If the user clicked the tour card's start button.
     */
    TourStartReason["START_TOUR_CLICKED"] = "start_tour_clicked";
})(TourStartReason || (TourStartReason = {}));
/**
 * The possible reasons why a tour could have ended. Purposefully not firing this event when the close button is clicked
 * or the {@link endTour} instance method is used.
 */
var TourEndReason;
(function (TourEndReason) {
    /**
     * If the user clicked the done button.
     */
    TourEndReason["DONE_CLICKED"] = "done_clicked";
})(TourEndReason || (TourEndReason = {}));
/**
 * A list of custom event types that can trigger a custom event.
 */
var CustomEventType;
(function (CustomEventType) {
    CustomEventType["BUTTON_ITEM_CLICKED"] = "buttonItemClicked";
})(CustomEventType || (CustomEventType = {}));
/**
 * The ways the user may interact with the feedback controls.
 */
var FeedbackInteractionType;
(function (FeedbackInteractionType) {
    /**
     * Indicates the details popup was opened after the user clicked one of the feedback buttons.
     */
    FeedbackInteractionType["DETAILS_OPENED"] = "detailsOpened";
    /**
     * Indicates the details popup was closed after the user clicked the "X" button to close it or if the user clicked the
     * feedback button that opened it.
     */
    FeedbackInteractionType["DETAILS_CLOSED"] = "detailsClosed";
    /**
     * Indicates feedback was submitted. This includes both when the details panel is open and submitted as well as when
     * the user clicks a feedback button and the details are not shown.
     */
    FeedbackInteractionType["SUBMITTED"] = "submitted";
})(FeedbackInteractionType || (FeedbackInteractionType = {}));

/**
 * The types of home screen backgrounds supported.
 */
var HomeScreenBackgroundType;
(function (HomeScreenBackgroundType) {
    HomeScreenBackgroundType["NONE"] = "none";
    HomeScreenBackgroundType["SOLID"] = "solid";
    HomeScreenBackgroundType["TOP_CORNER_OUT"] = "top_corner_out";
    HomeScreenBackgroundType["BOTTOM_UP"] = "bottom_up";
})(HomeScreenBackgroundType || (HomeScreenBackgroundType = {}));

/**
 * Status of the chain of thought step.
 */
var ChainOfThoughtStepStatus;
(function (ChainOfThoughtStepStatus) {
    ChainOfThoughtStepStatus["PROCESSING"] = "processing";
    ChainOfThoughtStepStatus["FAILURE"] = "failure";
    ChainOfThoughtStepStatus["SUCCESS"] = "success";
})(ChainOfThoughtStepStatus || (ChainOfThoughtStepStatus = {}));

var MinimizeButtonIconType;
(function (MinimizeButtonIconType) {
    /**
     * This shows an "X" icon.
     */
    MinimizeButtonIconType["CLOSE"] = "close";
    /**
     * This shows a "-" icon.
     */
    MinimizeButtonIconType["MINIMIZE"] = "minimize";
    /**
     * This shows an icon that indicates that the web chat can be collapsed into a side panel.
     */
    MinimizeButtonIconType["SIDE_PANEL_LEFT"] = "side-panel-left";
    /**
     * This shows an icon that indicates that the web chat can be collapsed into a side panel.
     */
    MinimizeButtonIconType["SIDE_PANEL_RIGHT"] = "side-panel-right";
})(MinimizeButtonIconType || (MinimizeButtonIconType = {}));
var ClosePanelIconDirection;
(function (ClosePanelIconDirection) {
    ClosePanelIconDirection["LEFT"] = "left";
    ClosePanelIconDirection["RIGHT"] = "right";
})(ClosePanelIconDirection || (ClosePanelIconDirection = {}));
/**
 * A string identifying what Carbon Theme we should base UI variables off of. Defaults to 'g10'. See
 * https://carbondesignsystem.com/guidelines/color/tokens.
 */
var CarbonTheme;
(function (CarbonTheme) {
    CarbonTheme["WHITE"] = "white";
    CarbonTheme["G10"] = "g10";
    CarbonTheme["G90"] = "g90";
    CarbonTheme["G100"] = "g100";
})(CarbonTheme || (CarbonTheme = {}));
/**
 * The different categories of errors that the system can record. These values are published for end user consumption.
 */
var OnErrorType;
(function (OnErrorType) {
    /**
     * Indicates an error loading the remote init config.
     */
    OnErrorType["INITIAL_CONFIG"] = "INITIAL_CONFIG";
    /**
     * Indicates an error loading the main config.
     */
    OnErrorType["OPEN_CONFIG"] = "OPEN_CONFIG";
    /**
     * Indicates an error sending a message to the assistant. This error is only generated after all retries have
     * failed and the system has given up.
     */
    OnErrorType["MESSAGE_COMMUNICATION"] = "MESSAGE_COMMUNICATION";
    /**
     * This indicates an error in one of the React components that occurs as part of rendering the UI.
     */
    OnErrorType["RENDER"] = "RENDER";
    /**
     * This indicates an error with the identity token.
     */
    OnErrorType["IDENTITY_TOKEN"] = "IDENTITY_TOKEN";
    /**
     * This indicates an error with the MCSP identity token.
     */
    OnErrorType["MCSP_IDENTITY_TOKEN"] = "MCSP_IDENTITY_TOKEN";
    /**
     * This indicates a known error with the configuration for the integration or assistant. The only current case
     * is when the web chat receives a connect_to_agent response and no service desk has been configured. These are
     * generally user errors.
     */
    OnErrorType["INTEGRATION_ERROR"] = "INTEGRATION_ERROR";
    /**
     * The agent in the service desk waited too long to pick up the request and the session has expired so history cannot
     * be fetched.
     */
    OnErrorType["AGENT_APP_EXPIRED_SESSION"] = "AGENT_APP_EXPIRED_SESSION";
    /**
     * This indicates that some error occurred while trying to hydrate web chat. This will prevent web chat from
     * functioning.
     */
    OnErrorType["HYDRATION"] = "HYDRATION";
    /**
     * Indicates that an attempt to load the preview link was made but security was enabled.
     */
    OnErrorType["PREVIEW_LINK_SECURITY"] = "PREVIEW_LINK_SECURITY";
})(OnErrorType || (OnErrorType = {}));
/**
 * The possible reasons that loading the web chat may fail.
 */
var CatastrophicErrorType;
(function (CatastrophicErrorType) {
    /**
     * Indicates it failed for some other reason.
     */
    CatastrophicErrorType["FAILED"] = "FAILED";
    /**
     * Indicates it failed because there was no skill attached to the assistant.
     */
    CatastrophicErrorType["NO_SKILL"] = "NO_SKILL";
    /**
     * Indicates it failed because the welcome node was throttled by the service.
     */
    CatastrophicErrorType["THROTTLED"] = "THROTTLED";
    /**
     * The agent in the service desk waited too long to pick up the request and the session has expired so history cannot
     * be fetched.
     */
    CatastrophicErrorType["AGENT_APP_EXPIRED_SESSION"] = "AGENT_APP_EXPIRED_SESSION";
})(CatastrophicErrorType || (CatastrophicErrorType = {}));

/**
 * Represents the different states for the availability of a human agent from a service desk.
 */
var AgentsOnlineStatus;
(function (AgentsOnlineStatus) {
    /**
     * Indicates that agents are online.
     */
    AgentsOnlineStatus["ONLINE"] = "online";
    /**
     * Indicates that no agents are online.
     */
    AgentsOnlineStatus["OFFLINE"] = "offline";
    /**
     * Indicates that it is unknown whether any agents are available. This may be because the service desk being used
     * doesn't support the ability to determine this information.
     */
    AgentsOnlineStatus["UNKNOWN"] = "unknown";
})(AgentsOnlineStatus || (AgentsOnlineStatus = {}));
/**
 * The possible state changes for a screen sharing request.
 */
var ScreenShareState;
(function (ScreenShareState) {
    /**
     * Indicates the screen sharing was accepted by the user.
     */
    ScreenShareState["ACCEPTED"] = "accepted";
    /**
     * Indicates the screen sharing was declined by the user.
     */
    ScreenShareState["DECLINED"] = "declined";
    /**
     * Indicates the screen sharing request was cancelled.
     */
    ScreenShareState["CANCELLED"] = "cancelled";
    /**
     * Indicates that screen sharing has ended.
     */
    ScreenShareState["ENDED"] = "ended";
})(ScreenShareState || (ScreenShareState = {}));
/**
 * The possible events that may have some form of error status.
 */
var ErrorType;
(function (ErrorType) {
    /**
     * This error is meant to be displayed while the user is attempting to connect to a service desk and before an
     * agent has joined. If this error is generated by the service desk, it is expected that the service desk will
     * treat the chat as having ended (or never started).
     */
    ErrorType[ErrorType["CONNECTING"] = 1] = "CONNECTING";
    /**
     * This is used to indicate the state of errors that can happen any time during a chat where the service desk
     * implementation has lost a connection to the back-end. If this error occurs while the user is waiting for an
     * agent to join, it will be treated as a {@link CONNECTING} error instead.
     */
    ErrorType[ErrorType["DISCONNECTED"] = 2] = "DISCONNECTED";
    /**
     * This error is used to report when there was an error sending a message to the agent.
     */
    ErrorType[ErrorType["USER_MESSAGE"] = 3] = "USER_MESSAGE";
})(ErrorType || (ErrorType = {}));
/**
 * These are the human agent specific message types.
 */
var AgentMessageType;
(function (AgentMessageType) {
    AgentMessageType["INLINE_ERROR"] = "inline_error";
    AgentMessageType["FROM_AGENT"] = "from_agent";
    AgentMessageType["FROM_USER"] = "from_user";
    AgentMessageType["AGENT_LEFT_CHAT"] = "agent_left_chat";
    AgentMessageType["AGENT_ENDED_CHAT"] = "agent_ended_chat";
    AgentMessageType["AGENT_JOINED"] = "agent_joined";
    AgentMessageType["RELOAD_WARNING"] = "user_connected_warning";
    AgentMessageType["TRANSFER_TO_AGENT"] = "transfer_to_agent";
    AgentMessageType["USER_ENDED_CHAT"] = "user_ended_chat";
    AgentMessageType["CHAT_WAS_ENDED"] = "chat_was_ended";
    AgentMessageType["DISCONNECTED"] = "disconnected";
    AgentMessageType["RECONNECTED"] = "reconnected";
    AgentMessageType["SHARING_REQUESTED"] = "sharing_requested";
    AgentMessageType["SHARING_ACCEPTED"] = "sharing_accepted";
    AgentMessageType["SHARING_DECLINED"] = "sharing_declined";
    AgentMessageType["SHARING_CANCELLED"] = "sharing_cancelled";
    AgentMessageType["SHARING_ENDED"] = "sharing_ended";
    AgentMessageType["SYSTEM"] = "system";
})(AgentMessageType || (AgentMessageType = {}));

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    if (typeof b !== "function" && b !== null)
        throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const e=new Set(["children","localName","ref","style","className"]),n=new WeakMap,t=(e,t,o,l,a)=>{const s=a?.[t];void 0===s?(e[t]=o,null==o&&t in HTMLElement.prototype&&e.removeAttribute(t)):o!==l&&((e,t,o)=>{let l=n.get(e);void 0===l&&n.set(e,l=new Map);let a=l.get(t);void 0!==o?void 0===a?(l.set(t,a={handleEvent:o}),e.addEventListener(t,a)):a.handleEvent=o:void 0!==a&&(l.delete(t),e.removeEventListener(t,a));})(e,s,o);},o=({react:n,tagName:o,elementClass:l,events:a,displayName:s})=>{const c=new Set(Object.keys(a??{})),r=n.forwardRef(((s,r)=>{const i=n.useRef(new Map),d=n.useRef(null),f={},u={};for(const[n,t]of Object.entries(s))e.has(n)?f["className"===n?"class":n]=t:c.has(n)||n in l.prototype?u[n]=t:f[n]=t;return n.useLayoutEffect((()=>{if(null===d.current)return;const e=new Map;for(const n in u)t(d.current,n,s[n],i.current.get(n),a),i.current.delete(n),e.set(n,s[n]);for(const[e,n]of i.current)t(d.current,e,void 0,n,a);i.current=e;})),n.useLayoutEffect((()=>{d.current?.removeAttribute("defer-hydration");}),[]),f.suppressHydrationWarning=!0,n.createElement(o,{...f,ref:n.useCallback((e=>{d.current=e,"function"==typeof r?r(e):null!==r&&(r.current=e);}),[r])})}));return r.displayName=s??l.name,r};

const useOnMount = (callback) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(callback, []);
};

const legacyCustomElement = (tagName, clazz) => {
    try {
        customElements.define(tagName, clazz);
    }
    catch (error) {
        // console.warn(`Attempting to re-define ${tagName}`);
    }
    // Cast as any because TS doesn't recognize the return type as being a
    // subtype of the decorated class when clazz is typed as
    // `Constructor<HTMLElement>` for some reason.
    // `Constructor<HTMLElement>` is helpful to make sure the decorator is
    // applied to elements however.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return clazz;
};
const standardCustomElement = (tagName, descriptor) => {
    const { kind, elements } = descriptor;
    return {
        kind,
        elements,
        // This callback is called once the class is otherwise fully defined
        finisher(clazz) {
            try {
                customElements.define(tagName, clazz);
            }
            catch (error) {
                // console.warn(`Attempting to re-define ${tagName}`);
            }
        },
    };
};
/**
 * Class decorator factory that defines the decorated class as a custom element.
 *
 * ```js
 * @carbonElement('my-element')
 * class MyElement extends LitElement {
 *   render() {
 *     return html``;
 *   }
 * }
 * ```
 *
 * @param tagName The tag name of the custom element to define.
 */
function carbonElement(tagName) {
    return (classOrDescriptor) => typeof classOrDescriptor === 'function'
        ? legacyCustomElement(tagName, classOrDescriptor)
        : standardCustomElement(tagName, classOrDescriptor);
}

export { AgentMessageType as A, BusEventType as B, CarbonTheme as C, ErrorType as E, FeedbackInteractionType as F, HomeScreenBackgroundType as H, IFrameItemDisplayOption as I, LauncherType as L, MessageInputType as M, OnErrorType as O, ScreenShareState as S, TourEndReason as T, ViewChangeReason as V, WriteableElementName as W, __decorate as _, AgentsOnlineStatus as a, ButtonItemType as b, carbonElement as c, ButtonItemKind as d, CatastrophicErrorType as e, ChainOfThoughtStepStatus as f, CornersType as g, CustomEventType as h, FileStatusValue as i, MessageResponseTypes as j, MessageSendSource as k, MinimizeButtonIconType as l, OptionItemPreference as m, TourStartReason as n, o, ViewType as p, ChatHeaderObjectType$1 as q, WidthOptions as r, InputFieldType as s, __assign as t, useOnMount as u, __extends as v, __rest as w, __spreadArray as x, ClosePanelIconDirection as y };
